import { useFormikContext } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import generalSettings from "@apis/generalSettings";
import { PAYMENT_METHODS } from "@utils/paymentConfigs";

function isTimeInSlot(slot: string) {
  try {
    const [startStr, endStr] = slot.split(" - ");
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const parseTimeToMinutes = (timeStr: string) => {
      const [time, period] = timeStr.split(" ");
      let [hours, minutes] = time.split(":").map(Number);
      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;
      return hours * 60 + minutes;
    };

    const startMinutes = parseTimeToMinutes(startStr);
    const endMinutes = parseTimeToMinutes(endStr);

    if (startMinutes <= endMinutes) {
      return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
    } else {
      // Over midnight case
      return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
    }
  } catch (e) {
    return false;
  }
}

const PaymentOptions = () => {
  const { values, setFieldValue } = useFormikContext<any>();
  const selectedOptions = values.paymentOptions || [];
  const [schedule, setSchedule] = useState<any>(null);

  const fetchPaymentSchedule = useCallback(async () => {
    try {
      const scheduleItem = await generalSettings.getByKey({
        state: "payment-settings",
        id: "payment-schedule",
        showErr: false,
      });

      if (scheduleItem) {
        setSchedule(scheduleItem.infos || null);
      }
    } catch (err) {
      console.error("Failed to fetch payment schedule:", err);
    }
  }, []);

  useEffect(() => {
    fetchPaymentSchedule();
  }, [fetchPaymentSchedule]);

  const currentActiveMethods = (() => {
    if (schedule?.slots?.length > 0) {
      const activeSlot = schedule.slots.find((slot: any) =>
        isTimeInSlot(slot.timeSlot)
      );
      if (activeSlot) {
        return activeSlot.methods;
      }
      return [];
    }
    return [];
  })();

  const toggleOption = (val: string) => {
    if (selectedOptions.includes(val)) {
      setFieldValue(
        "paymentOptions",
        selectedOptions.filter((item: string) => item !== val),
      );
    } else {
      setFieldValue("paymentOptions", [...selectedOptions, val]);
    }
  };

  const PaymentPill = ({
    value,
    label,
    icon: Icon,
    color,
  }: {
    value: string;
    label: string;
    icon: any;
    color: string;
  }) => {
    const isSelected = selectedOptions.includes(value);
    return (
      <div
        onClick={() => toggleOption(value)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px 16px",
          borderRadius: "24px",
          border: isSelected ? "1px solid #EF4444" : "1px solid #E2E8F0",
          background: isSelected ? "#FEF2F2" : "#FFFFFF",
          cursor: "pointer",
          transition: "all 0.2s ease",
          boxShadow: isSelected ? "0 1px 2px rgba(239, 68, 68, 0.1)" : "none",
        }}
      >
        <Icon size={16} color={isSelected ? color : "#64748B"} />
        <span
          style={{
            fontWeight: 600,
            fontSize: "13px",
            color: isSelected ? "#B91C1C" : "#334155",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
      </div>
    );
  };

  const visibleMethods = PAYMENT_METHODS.filter((method) =>
    currentActiveMethods.includes(method.key.toLowerCase())
  );

  return (
    <div
      style={{
        display: "flex",
        gap: "10px",
        marginBottom: "15px",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <label
        style={{
          fontSize: "13px",
          fontWeight: 500,
          color: "#64748B",
          marginRight: "5px",
        }}
      >
        Payment Methods:
      </label>
      {visibleMethods.length > 0 ? (
        visibleMethods.map((method) => (
          <PaymentPill
            key={method.key}
            value={method.key}
            label={method.label}
            icon={method.icon}
            color={method.color}
          />
        ))
      ) : (
        <span style={{ fontSize: "12px", color: "#64748B", fontStyle: "italic" }}>
          No payment methods available for the current time.
        </span>
      )}
    </div>
  );
};

export default PaymentOptions;
